import Quill from 'quill'

import { getPlaceholderData, isEncodedPlaceholder, QUILL_EDITOR_TEXT_INPUT_TYPE } from './textInputHelper'
import { isolatePasteInHistory, isQuillHistoryEntry, transformHistoryStack } from './quillHistoryEntries'

const Module = Quill.import('core/module')
const Clipboard = Quill.import('modules/clipboard')
const CodeBlock = Quill.import('formats/code-block')
const History = Quill.import('modules/history')
const SnowTheme = Quill.import('themes/snow')
const Uploader = Quill.import('modules/uploader')

// The chevron the old patched dist used for picker labels (replaces quill's sort-arrows icon).
export const PICKER_CHEVRON_SVG = `<svg
  class="ql-fill ql-stroke ql-chevron"
  viewBox="-1 -2 21 21">
  <path
  d="M 5.9902344,7.9902344 A 1.0001,1.0001 0 0 0 5.2929688,9.7070312 l 6.0000002,5.9999998 a 1.0001,1.0001 0 0 0 1.414062,0 l 6,-5.9999998 A 1.0001,1.0001 0 1 0 17.292969,8.2929688 L 12,13.585938 6.7070312,8.2929688 A 1.0001,1.0001 0 0 0 5.9902344,7.9902344 Z"/>
  </svg>
`

// Replaces the retired quill-1 dist patch (replacement_node_modules/quill). Every editor
// passes an app-encoded placeholder (`text#editorType#editorId#…`, see createPlaceholder);
// this module decodes it, tags the editor DOM with the per-editor classes the rest of the
// app looks up, and keeps the raw metadata off the visible placeholder. It must be listed
// in every <ReactQuill> modules config (`editorMeta: true`).
class EditorMeta extends Module {
    constructor(quill, options) {
        super(quill, options)
        const meta = getPlaceholderData(quill.options.placeholder || '')
        quill.editorMeta = meta
        quill.enablePasteListener = meta.editorType === QUILL_EDITOR_TEXT_INPUT_TYPE
        quill.appManagedCopy = true
        if (meta.editorId) {
            quill.container.classList.add(`ql-container-${meta.editorId}`)
            quill.root.classList.add(`ql-editor-${meta.editorId}`)
        }
        // Quill writes options.placeholder into the root's data-placeholder attribute
        // after module init, so stripping the metadata here keeps the visible text clean.
        quill.options.placeholder = meta.placeholderText || ''
        keepPlaceholderAttributeDecoded(quill)
    }
}

// AT-2438. Decoding at construction is NOT enough, because this module is not the only
// writer of the attribute the placeholder is actually rendered from
// (`.ql-editor.ql-blank::before { content: attr(data-placeholder) }`): react-quill-new's
// componentDidUpdate does
//
//     if (this.editor && prevProps.placeholder !== this.props.placeholder)
//         this.editor.root.dataset.placeholder = this.props.placeholder || ''
//
// which puts the RAW app-encoded prop (`text#editorType#editorId#…`) straight into the
// DOM, behind this module's back — the constructor never runs again for a live editor.
// So any change of the placeholder prop made the encoding visible to the user, e.g.
// "Type to add new comment#0#d53db6ac-e144-4fee-8fab-…". The chat composer changes it on
// every login whose device locale differs from the account language: `translate()` is
// resolved at render time against `i18n.locale`, which starts as the DEVICE language and
// is switched to `loggedUser.language` by `useTranslator` once the user doc arrives — so
// a German browser signed in to an English account re-renders the composer with a
// different placeholder string, and it leaked from that moment on.
//
// The encoding has to stay on the React prop: `EditorToolbar` and `getQuillEditorRef`
// read `<ReactQuill>.props.placeholder` to recover the editorId. So own the attribute
// instead — this module already owns "the encoding never becomes visible".
const decodePlaceholderAttribute = root => {
    const raw = root.getAttribute('data-placeholder')
    // Idempotent by construction: a decoded value is no longer encoded, so the write this
    // makes cannot re-trigger the observer into a loop.
    if (!isEncodedPlaceholder(raw)) return
    root.setAttribute('data-placeholder', getPlaceholderData(raw).placeholderText || '')
}

const keepPlaceholderAttributeDecoded = quill => {
    const root = quill.root
    if (!root || typeof root.getAttribute !== 'function') return
    decodePlaceholderAttribute(root)
    if (typeof MutationObserver === 'undefined') return
    // Scoped to the one attribute, and unreachable once the editor's DOM is dropped (the
    // observer is referenced only by this module, which hangs off the quill instance that
    // hangs off the detached node), so there is nothing to tear down — quill 2 gives a
    // module no destroy hook anyway.
    const observer = new MutationObserver(() => decodePlaceholderAttribute(root))
    observer.observe(root, { attributes: true, attributeFilter: ['data-placeholder'] })
    quill.placeholderAttributeObserver = observer
}

// Reads editor metadata whether or not the editorMeta module ran (headless editors
// created with plain `new Quill(node)` have no placeholder encoding).
export const getEditorMeta = quill =>
    quill.editorMeta ? quill.editorMeta : getPlaceholderData((quill.options && quill.options.placeholder) || '')

// Quill 2 (unlike 1.x) captures copy/cut/paste on the editor root itself. The app owns
// copy/cut on every real editor (onCopy in textInputHelper — quill's cut capture would
// delete the selection a second time), and owns paste for note-type editors (the notes
// editor attaches its own markdown/html paste pipeline).
class GatedClipboard extends Clipboard {
    onCaptureCopy(e, isCut) {
        if (this.quill.appManagedCopy) return
        super.onCaptureCopy(e, isCut)
    }

    onCapturePaste(e) {
        if (this.quill.enablePasteListener === false) return
        super.onCapturePaste(e)
    }

    // AT-2416. `Clipboard.convert` short-circuits to a verbatim `insert(text)` whenever the
    // clipboard carries no text/html, so quill 2 never runs the app's TEXT_NODE matcher over a
    // plain-text paste — quill 1 did, because it pushed the text through the DOM first. The
    // app's own "Copy link" button uses navigator.clipboard.writeText (text/plain ONLY), so a
    // copied task link pasted as raw URL text and only became a chip once a space was typed.
    // The autoformat module installs `convertPastedPlainText` on every text-input editor
    // (notes keep quill's own paste pipeline and never set it); routing through it also makes
    // the chip part of `pastedDelta`, so quill's own caret arithmetic is right by construction.
    convert({ html, text }, formats = {}) {
        const convertPlainText = this.quill.convertPastedPlainText
        const pastingIntoCodeBlock = formats[CodeBlock.blotName] != null
        if (!html && text && !pastingIntoCodeBlock && typeof convertPlainText === 'function') {
            return convertPlainText(text, formats)
        }
        return super.convert({ html, text }, formats)
    }

    // AT-2416. Quill places the post-paste caret at `delta.length() - range.length`, i.e. it
    // derives the position from the delta it was ABOUT to apply. That is only correct while
    // nothing else edits the document during the same update — and in this app something
    // always might: the autoformat module listens for `text-change` and rewrites a pasted
    // task/note/goal URL into a one-character embed (plus a leading space when the link opens
    // the line). A 46-character URL collapsing to one character leaves quill's index ~45
    // characters too far to the right, so pasting a task link mid-sentence dropped the caret
    // at the end of the line instead of behind the link. It only looked correct when the paste
    // happened at the end of the text, where quill clamps the overshoot to the last index.
    //
    // Everything a paste inserts lands at `range.index`, so the honest measure of how much was
    // inserted is how much the document actually grew, taken AFTER the update (and therefore
    // after every synchronous `text-change` listener has had its turn). For a paste nothing
    // rewrites, this resolves to exactly the index quill would have chosen.
    //
    // AT-2440: the update is wrapped so the paste is its own undo step. Quill coalesces
    // everything inside `history.delay` (1s) into one entry, which is right for typing and
    // wrong for a paste — "undo the paste" would also swallow the words typed just before it.
    onPaste(range, { text, html }) {
        const lengthBefore = this.quill.getLength()
        isolatePasteInHistory(this.quill, () => super.onPaste(range, { text, html }))
        const inserted = this.quill.getLength() - lengthBefore + range.length
        this.quill.setSelection(range.index + inserted, Quill.sources.SILENT)
        this.quill.scrollSelectionIntoView()
    }
}

// AT-2441. Quill 2 sends a PASTED image file through the uploader as well — see
// `Clipboard.onCapturePaste`, which calls `quill.uploader.upload(range, files)` whenever the
// clipboard carries files — and the uploader's default handler inserts a base64 data-URL
// `image` embed. The app's editors do not know that embed: `CustomTextInput3.updateText`
// serializes `attachment` / `customImageFormat` / `videoFormat` and nothing else, so a pasted
// screenshot appeared in the composer and was then silently dropped when the comment was
// posted. (Quill 1 had no such interception: the browser's own paste put an <img> in the
// contenteditable, which the autoformat matcher turned into a real attachment. This regressed
// at the quill 2 migration.)
//
// An editor that owns its attachments declares `quill.appManagedFileUpload` and gets the same
// named attachment a dropped file produces. Everything else — headless editors, the notes
// editor (which switches the module off entirely) — keeps quill's behaviour untouched.
class GatedUploader extends Uploader {
    upload(range, files) {
        const handleFiles = this.quill.appManagedFileUpload
        if (typeof handleFiles !== 'function') {
            super.upload(range, files)
            return
        }
        // Deliberately not filtered by `options.mimetypes` (png/jpeg): the app supports every
        // image type it can name plus arbitrary files, and a pasted pdf currently inserts
        // nothing at all because quill drops it here.
        handleFiles(range, files)
    }
}

// History with the beforeUndoRedo hook from the retired dist patch: the hook may consume
// the top stack entry itself (hashtag color changes push non-delta entries) and return
// false to cancel quill's own undo/redo.
//
// AT-2440: every method here now assumes the stack can hold BOTH quill's `{ delta, range }`
// entries and the app's delta-less marker entries, because quill's own code does not — see
// quillHistoryEntries.js. Before this, a single app entry could throw from three different
// places (the hook, quill's merge inside `record`, quill's `transformStack`), and a throw out
// of `undo()` is not a no-op: it skips the `preventDefault()` that quill's keydown and
// `beforeinput` handlers make afterwards, so the browser's native contenteditable undo runs
// instead and reverts something the user never asked it to.
class HookedHistory extends History {
    undo() {
        if (this.consumeAppHistoryEntry('undo', 'redo')) return
        super.undo()
    }

    redo() {
        if (this.consumeAppHistoryEntry('redo', 'undo')) return
        super.redo()
    }

    // Returns true when the app claimed the top entry and quill must not act on it.
    consumeAppHistoryEntry(startAction, endAction) {
        const hook = this.options.beforeUndoRedo
        if (typeof hook === 'function') {
            try {
                if (hook(this.stack, startAction, endAction) === false) return true
            } catch (error) {
                // Swallowed on purpose: whatever the hook wanted to do is less important than
                // undo staying in quill's hands. Letting this escape is what AT-2440 was.
                console.error('[quill history] beforeUndoRedo failed', error)
            }
        }
        // Never hand quill an entry it cannot invert. Anything still on top that the hook did
        // not claim carries no delta, so quill's `change()` would throw on it forever, wedging
        // the whole stack behind it.
        const stack = this.stack[startAction]
        while (stack.length > 0 && !isQuillHistoryEntry(stack[stack.length - 1])) stack.pop()
        return false
    }

    record(changeDelta, oldDelta) {
        // Inside `options.delay` quill merges by popping the previous entry and composing the
        // two deltas. An app entry has none, so cut the merge window instead.
        const topEntry = this.stack.undo[this.stack.undo.length - 1]
        if (topEntry && !isQuillHistoryEntry(topEntry)) this.cutoff()
        super.record(changeDelta, oldDelta)
    }

    transform(delta) {
        transformHistoryStack(this.stack.undo, delta)
        transformHistoryStack(this.stack.redo, delta)
    }
}

export const decoratePicker = picker => {
    const { select } = picker
    const items = picker.container.querySelectorAll('.ql-picker-item')
    Array.from(select.options).forEach((option, index) => {
        const item = items[index]
        if (!item) return
        if (option.hasAttribute('data-html')) {
            item.innerHTML = option.getAttribute('data-html')
        }
        // Quill 1 stamped data-value onto items whenever the option carried a value
        // attribute — including value="" — while quill 2 skips falsy values. The app
        // CSS keys the header picker's layout and its text-hiding rules off
        // [data-value=''], so restore the quill-1 behavior.
        if (option.hasAttribute('value') && !item.hasAttribute('data-value')) {
            item.setAttribute('data-value', option.getAttribute('value'))
        }
    })
    if (select.hasAttribute('data-html')) {
        picker.label.innerHTML += select.getAttribute('data-html')
    }
    if (select.classList.contains('ql-header')) {
        // The header picker label mirrors the selected item's icon (the quill-1 patched
        // look) instead of quill's default sort-arrows glyph. Two quill-2 deltas matter
        // here: selectItem() now copies the item's data-label onto the LABEL — which the
        // vendored CSS renders as literal text via `content: attr(data-label)`, so it
        // must be stripped — and selectItem() early-returns when the selection is
        // unchanged, so the label is synced from the current .ql-selected item rather
        // than from selectItem's argument.
        const syncLabel = () => {
            picker.label.removeAttribute('data-label')
            const selectedItem = picker.container.querySelector('.ql-picker-item.ql-selected')
            // The label must ALWAYS carry data-value (empty for "Normal text") — the
            // app CSS hides the fallback ::before text via [data-value='']::before
            // { display: none }, which quill 1 satisfied by keeping the empty
            // attribute and quill 2 breaks by removing it.
            picker.label.setAttribute('data-value', (selectedItem && selectedItem.getAttribute('data-value')) || '')
            const selectedIcon = selectedItem && selectedItem.querySelector('.ql-header-item-icon')
            picker.label.innerHTML = ''
            if (selectedIcon) {
                picker.label.appendChild(selectedIcon.cloneNode(true))
            } else {
                picker.label.innerHTML = PICKER_CHEVRON_SVG
            }
        }
        const originalSelectItem = picker.selectItem.bind(picker)
        picker.selectItem = (item, trigger) => {
            originalSelectItem(item, trigger)
            syncLabel()
        }
        syncLabel()
    }
}

// Snow theme with the app's toolbar affordances: `data-html` payloads on toolbar buttons
// and picker <option>s render as rich labels (shortcut hints, color swatches), and the
// built-in cmd/ctrl+K link binding is dropped — the app binds ctrl+k itself and inserts
// its own url embed instead of quill's link tooltip flow.
class CustomSnowTheme extends SnowTheme {
    extendToolbar(toolbar) {
        super.extendToolbar(toolbar)
        if (toolbar.container == null) return
        toolbar.container.querySelectorAll('button[data-html]').forEach(button => {
            button.innerHTML += button.getAttribute('data-html')
        })
        ;(this.pickers || []).forEach(decoratePicker)
        const kBindings = this.quill.keyboard.bindings['k']
        if (kBindings) {
            this.quill.keyboard.bindings['k'] = kBindings.filter(binding => !binding.shortKey)
        }
    }
}

// Quill 2's `focus()` and `setSelection()` end in `scrollSelectionIntoView()`,
// which walks EVERY scrollable ancestor of the editor up to `document.body` and
// scrolls each one so the caret rect is inside it — finishing with a
// `window.scrollBy` for the document itself. Quill 1 did the opposite: it saved
// its own `scrollingContainer.scrollTop` around `selection.focus()`, put it back
// afterwards, and only ever scrolled that one container. The app relied on that:
// an inline task editor is mounted with `autoFocus`, so since the Quill 2
// migration simply opening a task line can scroll the task list, a detailed view
// or the page underneath it — the "jumping around" in AT-2220. It fires up to
// three times per open (mount focus, `setSelection` after the initial text, and
// the parent's own `focus()`), and again on every mention insert and popup
// dismiss.
//
// Restore the Quill 1 contract without reimplementing any of Quill's geometry:
// remember where every scroll container ABOVE the editor's own scroll boundary
// was, let Quill do exactly what it does today, then put those containers back.
// Everything at or inside the boundary — the `.ql-editor`'s own scrollbar, the
// editor's own scroll view — still follows the caret unchanged.
//
// The boundary is opt-in per editor (`quill.scrollBoundaryElement`, set by
// CustomTextInput3). An editor that does not set one — the notes document
// editor, whose page scroller SHOULD follow the caret — keeps Quill's stock
// behaviour, so this cannot regress it.
export const confineScrollRectIntoView = original => {
    function scrollRectIntoViewWithinBoundary(rect) {
        const boundary = this.scrollBoundaryElement
        if (!boundary || typeof document === 'undefined' || !boundary.parentElement) {
            return original.call(this, rect)
        }

        const frozen = []
        for (let node = boundary.parentElement; node; node = node.parentElement) {
            frozen.push({ node, top: node.scrollTop, left: node.scrollLeft })
        }
        const view = typeof window !== 'undefined' ? window : null
        const pageX = view ? view.scrollX : 0
        const pageY = view ? view.scrollY : 0

        try {
            return original.call(this, rect)
        } finally {
            // Plain assignment, never scrollTo(): `html { scroll-behavior: smooth }`
            // would otherwise animate the restore and show the very jump we are
            // suppressing. Assignment on these containers is instant — they do not
            // set scroll-behavior themselves.
            frozen.forEach(({ node, top, left }) => {
                if (node.scrollTop !== top) node.scrollTop = top
                if (node.scrollLeft !== left) node.scrollLeft = left
            })
            if (view && (view.scrollX !== pageX || view.scrollY !== pageY)) {
                view.scrollTo({ left: pageX, top: pageY, behavior: 'instant' })
            }
        }
    }
    scrollRectIntoViewWithinBoundary.confinesScrollToEditor = true
    return scrollRectIntoViewWithinBoundary
}

Quill.prototype.scrollRectIntoView = confineScrollRectIntoView(Quill.prototype.scrollRectIntoView)

Quill.register('modules/editorMeta', EditorMeta, true)
Quill.register('modules/clipboard', GatedClipboard, true)
Quill.register('modules/history', HookedHistory, true)
Quill.register('modules/uploader', GatedUploader, true)
Quill.register('themes/snow', CustomSnowTheme, true)
